/* Theme custom file */
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Floating "back to top" button for long product grids.
 *
 * Visible once the user has scrolled ~1.5 viewport heights past the anchor
 * (the grid's filters bar); a single threshold also guarantees the button is
 * gone well before the anchor re-enters the viewport. Clicking scrolls back
 * to the anchor, whose `scroll-margin-top` keeps it clear of sticky bars.
 *
 * Attributes:
 *   anchor-id  id of the element marking the top of the grid.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<Refs>}
 */
class BackToTop extends Component {
  requiredRefs = ['button'];

  #ticking = false;
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.button.addEventListener('click', this.#scrollToAnchor);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.button?.removeEventListener('click', this.#scrollToAnchor);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  /**
   * Resolved on every use rather than cached at connect: the section can be
   * re-rendered around module init, and a connect-time lookup that misses
   * once would leave the button permanently dead.
   */
  get #anchor() {
    return document.getElementById(this.getAttribute('anchor-id') ?? '');
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  };

  #update = () => {
    const anchor = this.#anchor;
    if (!anchor) {
      this.#visible = false;
      this.removeAttribute('visible');
      return;
    }

    const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
    const visible = window.scrollY > anchorTop + window.innerHeight * 1.5;
    if (visible === this.#visible) return;
    this.#visible = visible;
    this.toggleAttribute('visible', visible);
  };

  #scrollToAnchor = () => {
    this.#anchor?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };
}

if (!customElements.get('back-to-top')) {
  customElements.define('back-to-top', BackToTop);
}
